import { isVideoStation } from '@app/core/stations/helpers';
import { Station } from '@app/core/types/stations';
import { Box } from '@mui/material';
import { AuditMatchAudio } from '../../types';
import { VerifyMatchPlaybackRange } from './verify-match-range';
import { VerifyMatchTarget } from './verify-match-types';
import { VerifyMatchBroadcastPlayer } from './VerifyMatchBroadcastPlayer';
import { VerifyMatchCreativePlayer } from './VerifyMatchCreativePlayer';

export interface VerifyMatchPlayersProps {
  station: Station;
  target: VerifyMatchTarget;
  range: VerifyMatchPlaybackRange;
  /** Reference creative for spot matches; omitted for mentions or matches without audio. */
  creativeAudio?: AuditMatchAudio | null;
  /** Optional broadcast video URL override (e.g. Storybook demo media). */
  broadcastVideoSrc?: string;
  /** Optional creative media URL override (e.g. Storybook demo media). */
  creativeMediaUrl?: string;
}

/**
 * Renders broadcast (and optional creative) players.
 * Video stations with a creative show both side by side for easier comparison.
 */
export function VerifyMatchPlayers({
  station,
  target,
  range,
  creativeAudio,
  broadcastVideoSrc,
  creativeMediaUrl,
}: VerifyMatchPlayersProps) {
  const showCreative = creativeAudio != null;
  const sideBySide = showCreative && isVideoStation(station);

  const broadcast = (
    <VerifyMatchBroadcastPlayer
      station={station}
      target={target}
      range={range}
      videoSrc={broadcastVideoSrc}
    />
  );

  if (!showCreative || !creativeAudio) {
    return broadcast;
  }

  const creative = (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <VerifyMatchCreativePlayer
        stationId={station.id}
        audio={creativeAudio}
        mediaUrl={creativeMediaUrl}
      />
    </Box>
  );

  if (sideBySide) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>{broadcast}</Box>
        {creative}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {broadcast}
      {creative}
    </Box>
  );
}
